<template>
  <div :class="['property-editor', darkMode ? 'dark-mode' : 'light-mode']">
    <a-form layout="vertical">
      <!-- Step 속성 -->
      <template v-if="item.stepId">
        <a-form-item label="스텝 이름">
          <a-input
            v-model:value="localItem.stepName"
            placeholder="예: 메인 단계 (비워두면 'Unnamed Step'으로 저장됩니다)"
          />
        </a-form-item>

        <a-form-item label="스텝 타입">
          <a-select v-model:value="localItem.stepType" style="width: 100%">
            <a-select-option value="intro">
              <badge-color color="green" /> Intro - 시작 단계
            </a-select-option>
            <a-select-option value="main">
              <badge-color color="blue" /> Main - 메인 단계
            </a-select-option>
            <a-select-option value="quiz">
              <badge-color color="cyan" /> Quiz - 퀴즈 단계
            </a-select-option>
            <a-select-option value="form">
              <badge-color color="orange" /> Form - 입력 단계
            </a-select-option>
            <a-select-option value="result">
              <badge-color color="purple" /> Result - 결과 단계
            </a-select-option>
            <a-select-option value="outro">
              <badge-color color="gray" /> Outro - 종료 단계
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="활성 상태">
          <a-switch v-model:checked="localItem.isActive" />
        </a-form-item>

        <a-form-item label="순서">
          <a-input-number v-model:value="localItem.order" :min="1" style="width: 100%" />
        </a-form-item>
      </template>

      <!-- Section 속성 -->
      <template v-else-if="item.sectionId">
        <a-form-item label="섹션 이름">
          <a-input v-model:value="localItem.sectionName" placeholder="예: 메인 섹션" />
        </a-form-item>

        <a-form-item label="섹션 ID">
          <a-input :value="localItem.sectionId" disabled />
        </a-form-item>

        <a-form-item label="섹션 타입">
          <a-radio-group v-model:value="localItem.sectionType" style="width: 100%">
            <a-space direction="vertical">
              <a-radio value="header">
                <block-outlined /> Header - 페이지 헤더
              </a-radio>
              <a-radio value="hero">
                <picture-outlined /> Hero - 메인 비주얼
              </a-radio>
              <a-radio value="content">
                <container-outlined /> Content - 콘텐츠
              </a-radio>
              <a-radio value="quiz">
                <question-circle-outlined /> Quiz - 퀴즈
              </a-radio>
              <a-radio value="form">
                <form-outlined /> Form - 입력 폼
              </a-radio>
              <a-radio value="custom">
                <profile-outlined /> Custom - 사용자 정의
              </a-radio>
            </a-space>
          </a-radio-group>
        </a-form-item>

        <!-- 섹션 메타데이터 설정 -->
        <!-- 퀴즈 타입에서는 특별한 설정 없음 -->

        <a-form-item label="순서">
          <a-input-number v-model:value="localItem.order" :min="1" style="width: 100%" />
        </a-form-item>
      </template>

      <!-- Component 속성 -->
      <template v-else-if="item.id">
        <a-form-item label="컴포넌트 ID">
          <a-input v-model:value="localItem.id" disabled />
        </a-form-item>

        <a-form-item label="컴포넌트 타입">
          <a-tag :color="getComponentColor(localItem.type)">
            {{ localItem.type }}
          </a-tag>
        </a-form-item>

        <template v-if="localItem.type === 'text'">
          <a-form-item label="텍스트 내용" required>
            <a-textarea
              v-model:value="localItem.content.txt"
              placeholder="표시할 텍스트를 입력하세요"
              :rows="4"
            />
          </a-form-item>
        </template>

        <template v-if="localItem.type === 'button'">
          <a-form-item label="버튼 텍스트" required>
            <a-input
              v-model:value="localItem.content.txt"
              placeholder="버튼에 표시할 텍스트를 입력하세요"
            />
          </a-form-item>
          <a-form-item label="버튼 링크 (URL)">
            <a-input
              v-model:value="localItem.content.link"
              placeholder="https://example.com (선택사항)"
            />
          </a-form-item>
          <a-form-item label="버튼 스타일">
            <a-select v-model:value="localItem.content.style" placeholder="버튼 스타일 선택">
              <a-select-option value="primary">Primary</a-select-option>
              <a-select-option value="secondary">Secondary</a-select-option>
              <a-select-option value="danger">Danger</a-select-option>
            </a-select>
          </a-form-item>
        </template>

        <template v-else-if="localItem.type === 'text'">
          <a-form-item label="텍스트 내용">
            <a-textarea
              v-model:value="localItem.content.txt"
              :rows="4"
              placeholder="텍스트 내용"
            />
          </a-form-item>
        </template>

        <template v-else-if="localItem.type === 'image'">
          <a-form-item label="이미지" required>
            <!-- 이미지가 있을 경우 미리보기 -->
            <div v-if="localItem.content.imageUrl" class="image-preview-container">
              <img
                :src="localItem.content.imageUrl"
                alt="미리보기"
                style="max-width: 100%; height: auto; border-radius: 4px;"
              />
              <div style="margin-top: 8px; display: flex; gap: 8px;">
                <a-button
                  size="small"
                  type="text"
                  danger
                  @click="removeImage"
                >
                  <delete-outlined /> 이미지 제거
                </a-button>
                <a-button
                  size="small"
                  @click="openResourceModalForImage"
                >
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>
            </div>

            <!-- 이미지가 없을 경우 업로드 옵션 -->
            <div v-else>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                <a-radio-group v-model:value="imageUploadMode" size="small">
                  <a-radio-button value="url">URL 입력</a-radio-button>
                  <a-radio-button value="upload">파일 업로드</a-radio-button>
                </a-radio-group>
                <a-button
                  size="small"
                  @click="openResourceModalForImage"
                >
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>

              <!-- URL 입력 모드 -->
              <div v-if="imageUploadMode === 'url'">
                <a-input
                  v-model:value="tempImageUrl"
                  placeholder="https://example.com/image.png"
                  @pressEnter="applyImageUrl"
                />
                <a-button
                  type="primary"
                  size="small"
                  style="margin-top: 8px;"
                  @click="applyImageUrl"
                >
                  URL 적용
                </a-button>
              </div>

              <!-- 파일 업로드 모드 -->
              <div v-else>
                <a-upload
                  :before-upload="beforeUpload"
                  :custom-request="customUpload"
                  :show-upload-list="false"
                  accept="image/*"
                >
                  <a-button :loading="imageUploading">
                    <upload-outlined /> 이미지 선택
                  </a-button>
                </a-upload>
                <div v-if="imageUploading" style="margin-top: 8px;">
                  <a-spin size="small" /> 업로드 중...
                </div>
              </div>
            </div>
          </a-form-item>
          <a-form-item label="대체 텍스트">
            <a-input
              v-model:value="localItem.content.alt"
              placeholder="이미지를 표시할 수 없을 때 보여줄 텍스트"
            />
          </a-form-item>
          <a-form-item label="이미지 클릭 시 링크">
            <a-input
              v-model:value="localItem.content.link"
              placeholder="https://example.com (선택사항)"
            />
          </a-form-item>
          <a-form-item label="이미지 크기">
            <a-row :gutter="8">
              <a-col :span="12">
                <a-input
                  v-model:value="localItem.content.width"
                  placeholder="너비 (px 또는 %)"
                />
              </a-col>
              <a-col :span="12">
                <a-input
                  v-model:value="localItem.content.height"
                  placeholder="높이 (px 또는 %)"
                />
              </a-col>
            </a-row>
          </a-form-item>
        </template>

        <template v-else-if="localItem.type === 'backgroundImage'">
          <a-form-item label="배경 이미지" required>
            <!-- 이미지가 있을 경우 미리보기 -->
            <div v-if="localItem.content.imageUrl" class="image-preview-container">
              <img
                :src="localItem.content.imageUrl"
                alt="배경 미리보기"
                style="max-width: 100%; height: auto; border-radius: 4px;"
              />
              <div style="margin-top: 8px; display: flex; gap: 8px;">
                <a-button
                  size="small"
                  type="text"
                  danger
                  @click="removeBackgroundImage"
                >
                  <delete-outlined /> 이미지 제거
                </a-button>
                <a-button
                  size="small"
                  @click="openResourceModalForBackground"
                >
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>
            </div>

            <!-- 이미지가 없을 경우 업로드 옵션 -->
            <div v-else>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                <a-radio-group v-model:value="imageUploadMode" size="small">
                  <a-radio-button value="url">URL 입력</a-radio-button>
                  <a-radio-button value="upload">파일 업로드</a-radio-button>
                </a-radio-group>
                <a-button
                  size="small"
                  @click="openResourceModalForBackground"
                >
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>

              <!-- URL 입력 모드 -->
              <div v-if="imageUploadMode === 'url'">
                <a-input
                  v-model:value="tempImageUrl"
                  placeholder="https://example.com/background.png"
                  @pressEnter="applyBackgroundImageUrl"
                />
                <a-button
                  type="primary"
                  size="small"
                  style="margin-top: 8px;"
                  @click="applyBackgroundImageUrl"
                >
                  URL 적용
                </a-button>
              </div>

              <!-- 파일 업로드 모드 -->
              <div v-else>
                <a-upload
                  :before-upload="beforeUpload"
                  :custom-request="customBackgroundUpload"
                  :show-upload-list="false"
                  accept="image/*"
                >
                  <a-button :loading="imageUploading">
                    <upload-outlined /> 이미지 선택
                  </a-button>
                </a-upload>
                <div v-if="imageUploading" style="margin-top: 8px;">
                  <a-spin size="small" /> 업로드 중...
                </div>
              </div>
            </div>
          </a-form-item>
        </template>

        <template v-else-if="localItem.type === 'survey'">
          <a-form-item label="설문 텍스트" required>
            <a-textarea
              v-model:value="localItem.content.txt"
              placeholder="설문 텍스트를 입력하세요"
              :rows="4"
            />
          </a-form-item>
        </template>

        <template v-else-if="localItem.type === 'link'">
          <a-form-item label="링크 URL">
            <a-input
              v-model:value="localItem.content.link"
              placeholder="https://..."
            />
          </a-form-item>
          <a-form-item label="링크 텍스트">
            <a-input
              v-model:value="localItem.content.txt"
              placeholder="링크 텍스트"
            />
          </a-form-item>
        </template>

        <a-form-item label="활성 상태">
          <a-switch v-model:checked="localItem.isActive" />
        </a-form-item>
      </template>
    </a-form>

    <div :class="['property-editor-footer', darkMode ? 'dark' : 'light']">
      <a-button @click="$emit('cancel')">취소</a-button>
      <a-button type="primary" @click="handleSave" class="yellow-button">
        저장
      </a-button>
    </div>

    <!-- 자료실 모달 -->
    <EventResourceModal
      :open="showResourceModal"
      :api="eventApi"
      @select="handleFileSelect"
      @cancel="showResourceModal = false"
    />
  </div>
</template>

<script>
import { ref, reactive, watch, getCurrentInstance, toRef } from 'vue';
import { message } from 'ant-design-vue';
import {
  BlockOutlined,
  PictureOutlined,
  ThunderboltOutlined,
  ContainerOutlined,
  QuestionCircleOutlined,
  FormOutlined,
  ProfileOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined
} from '@ant-design/icons-vue';
import EventResourceModal from '../../EventResourceModal.vue';
import eventsApis from '@/views/Events/api';

export default {
  name: 'PropertyEditor',
  components: {
    BlockOutlined,
    PictureOutlined,
    ThunderboltOutlined,
    ContainerOutlined,
    QuestionCircleOutlined,
    FormOutlined,
    ProfileOutlined,
    DeleteOutlined,
    PlusOutlined,
    UploadOutlined,
    EventResourceModal
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    darkMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const app = getCurrentInstance().appContext.config.globalProperties;
    const localItem = ref(JSON.parse(JSON.stringify(props.item)));

    // API 인스턴스 생성
    const instance = getCurrentInstance();
    const apiInstance = instance?.proxy?.$apiInstance;
    const store = instance?.proxy?.$store;
    const eventApi = eventsApis(apiInstance, store);

    // 이미지 업로드 관련 상태
    const imageUploadMode = ref('url');
    const imageUploading = ref(false);
    const tempImageUrl = ref('');

    // 자료실 모달 관련 상태
    const showResourceModal = ref(false);
    const selectingField = ref(''); // 'image' 또는 'backgroundImage'

    // Props 변경 시 로컬 아이템 업데이트
    watch(() => props.item, (newItem) => {
      localItem.value = JSON.parse(JSON.stringify(newItem));
    }, { deep: true });

    // 메타데이터 초기화
    if (localItem.value.sectionId) {
      if (!localItem.value.metadata) {
        localItem.value.metadata = {};
      }
      if (localItem.value.sectionType === 'header') {
        if (!localItem.value.metadata.title) localItem.value.metadata.title = '';
        if (!localItem.value.metadata.subtitle) localItem.value.metadata.subtitle = '';
      } else if (localItem.value.sectionType === 'content') {
        if (!localItem.value.metadata.content) localItem.value.metadata.content = '';
      } else if (localItem.value.sectionType === 'quiz') {
        if (!localItem.value.metadata.quizCompleteCardImg) {
          localItem.value.metadata.quizCompleteCardImg = '';
        }
      }
    }

    // 컴포넌트 색상
    const getComponentColor = (type) => {
      const colors = {
        button: 'blue',
        text: 'green',
        image: 'orange',
        link: 'purple'
      };
      return colors[type] || 'default';
    };

    // Badge 색상 컴포넌트
    const BadgeColor = (props) => {
      return <span style={`display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${props.color}; margin-right: 8px;`}></span>;
    };


    // 이미지 제거
    const removeImage = () => {
      localItem.value.content.imageUrl = '';
    };

    // 배경 이미지 제거
    const removeBackgroundImage = () => {
      localItem.value.content.imageUrl = '';
    };

    // URL 적용
    const applyImageUrl = () => {
      if (tempImageUrl.value) {
        localItem.value.content.imageUrl = tempImageUrl.value;
        tempImageUrl.value = '';
      }
    };

    // 배경 이미지 URL 적용
    const applyBackgroundImageUrl = () => {
      if (tempImageUrl.value) {
        localItem.value.content.imageUrl = tempImageUrl.value;
        tempImageUrl.value = '';
      }
    };

    // 파일 업로드 전 검증
    const beforeUpload = (file) => {
      const isImage = file.type.indexOf('image/') === 0;
      if (!isImage) {
        message.error('이미지 파일만 업로드 가능합니다!');
        return false;
      }

      // 배경이미지는 크기 제한 없음
      if (localItem.value.type !== 'backgroundImage') {
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
          message.error('이미지는 5MB 이하여야 합니다!');
          return false;
        }
      }

      return true;
    };

    // 커스텀 업로드 처리 - Azure Blob Storage 사용
    const customUpload = async ({ file }) => {
      try {
        imageUploading.value = true;

        // FormData 생성
        const formData = new FormData();
        formData.append('container', 'certilife');
        formData.append('files', file);

        // Azure Blob Storage 업로드 API 호출
        const response = await app.$apiInstance.post(
          '/object-storages/azure/files/upload',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-Azure-Container': 'certilife'
            },
            timeout: 120000
          }
        );

        // 업로드 성공 처리
        if (response.data && response.data.success && response.data.data && response.data.data.results && response.data.data.results.length > 0) {
          const uploadedFileUrl = response.data.data.results[0].url;
          localItem.value.content.imageUrl = uploadedFileUrl;
          message.success('이미지가 성공적으로 업로드되었습니다.');
        } else {
          throw new Error('업로드 응답이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('이미지 업로드 실패:', error);
        message.error('이미지 업로드에 실패했습니다.');
      } finally {
        imageUploading.value = false;
      }
    };

    // 배경 이미지 커스텀 업로드 처리 - Azure Blob Storage 사용
    const customBackgroundUpload = async ({ file }) => {
      try {
        imageUploading.value = true;

        // FormData 생성
        const formData = new FormData();
        formData.append('container', 'certilife');
        formData.append('files', file);

        // Azure Blob Storage 업로드 API 호출
        const response = await app.$apiInstance.post(
          '/object-storages/azure/files/upload',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-Azure-Container': 'certilife'
            },
            timeout: 120000
          }
        );

        // 업로드 성공 처리
        if (response.data && response.data.success && response.data.data && response.data.data.results && response.data.data.results.length > 0) {
          const uploadedFileUrl = response.data.data.results[0].url;
          localItem.value.content.imageUrl = uploadedFileUrl;
          message.success('배경 이미지가 성공적으로 업로드되었습니다.');
        } else {
          throw new Error('업로드 응답이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('배경 이미지 업로드 실패:', error);
        message.error('배경 이미지 업로드에 실패했습니다.');
      } finally {
        imageUploading.value = false;
      }
    };

    // Survey 타입 컴포넌트 초기화
    watch(() => localItem.value.type, (newType) => {
      if (newType === 'survey') {
        // survey 타입으로 변경 시 기본 텍스트 설정
        if (!localItem.value.content.txt) {
          localItem.value.content.txt = '';
        }
      }
    }, { immediate: true });

    // 자료실 모달 열기
    const openResourceModalForImage = () => {
      selectingField.value = 'image';
      showResourceModal.value = true;
    };

    const openResourceModalForBackground = () => {
      selectingField.value = 'backgroundImage';
      showResourceModal.value = true;
    };

    // 파일 선택 핸들러
    const handleFileSelect = (file) => {
      if (selectingField.value === 'image') {
        localItem.value.content.imageUrl = file.url;
        message.success(`이미지 선택: ${file.name}`);
      } else if (selectingField.value === 'backgroundImage') {
        localItem.value.content.imageUrl = file.url;
        message.success(`배경 이미지 선택: ${file.name}`);
      }
      showResourceModal.value = false;
    };

    // 저장
    const handleSave = () => {
      // 스텝 이름이 비어있으면 기본값 설정
      if (localItem.value.stepId && (!localItem.value.stepName || !localItem.value.stepName.trim())) {
        localItem.value.stepName = 'Unnamed Step';
      }
      // 섹션 이름이 비어있으면 기본값 설정
      if (localItem.value.sectionId && (!localItem.value.sectionName || !localItem.value.sectionName.trim())) {
        localItem.value.sectionName = 'Unnamed Section';
      }
      emit('save', localItem.value);
    };

    return {
      localItem,
      imageUploadMode,
      imageUploading,
      tempImageUrl,
      eventApi,
      showResourceModal,
      selectingField,
      darkMode: toRef(props, 'darkMode'),
      getComponentColor,
      BadgeColor,
      removeImage,
      removeBackgroundImage,
      applyImageUrl,
      applyBackgroundImageUrl,
      beforeUpload,
      customUpload,
      customBackgroundUpload,
      openResourceModalForImage,
      openResourceModalForBackground,
      handleFileSelect,
      handleSave
    };
  }
};
</script>

<style lang="scss" scoped>
.property-editor {
  // 다크 모드 스타일
  &.dark-mode {
    color: #fff;

    // 폼 라벨
    :deep(.ant-form-item-label > label) {
      color: #fff !important;
    }

    // 입력 필드
    :deep(.ant-input),
    :deep(.ant-input-number),
    :deep(.ant-input-number-input),
    :deep(.ant-textarea),
    :deep(.ant-select-selector),
    :deep(.ant-select-selection-item) {
      background-color: #3a3a3a !important;
      color: #fff !important;
      border-color: #555 !important;
    }

    :deep(.ant-input::placeholder),
    :deep(.ant-textarea::placeholder),
    :deep(.ant-input-number-input::placeholder) {
      color: #aaa !important;
    }

    :deep(.ant-select-arrow) {
      color: #aaa !important;
    }

    // 비활성화된 입력 필드
    :deep(.ant-input-disabled),
    :deep(.ant-input[disabled]) {
      background-color: #2a2a2a !important;
      color: #888 !important;
    }

    // 라디오 버튼
    :deep(.ant-radio-wrapper) {
      color: #fff !important;
    }

    :deep(.ant-radio-button-wrapper) {
      background-color: #3a3a3a !important;
      color: #fff !important;
      border-color: #555 !important;

      &:hover {
        color: #40a9ff !important;
      }

      &.ant-radio-button-wrapper-checked {
        background-color: #1890ff !important;
        border-color: #1890ff !important;
      }
    }

    // 스위치
    :deep(.ant-switch) {
      background-color: rgba(255, 255, 255, 0.25);
    }

    :deep(.ant-switch-checked) {
      background-color: #1890ff;
    }

    // 태그
    :deep(.ant-tag) {
      background-color: #3a3a3a;
      border-color: #555;
    }

    // 버튼
    :deep(.ant-btn-default) {
      background-color: #3a3a3a !important;
      color: #fff !important;
      border-color: #555 !important;

      &:hover {
        border-color: #40a9ff !important;
        color: #40a9ff !important;
      }
    }

    :deep(.ant-btn-text) {
      color: #fff !important;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1) !important;
      }

      &.ant-btn-dangerous {
        color: #ff4d4f !important;
      }
    }

    // 업로드 버튼
    :deep(.ant-upload) {
      .ant-btn {
        background-color: #3a3a3a !important;
        color: #fff !important;
        border-color: #555 !important;
      }
    }

    // 스핀
    :deep(.ant-spin) {
      color: #fff;
    }

    // Select 드롭다운 (옵션은 별도 처리 필요)
    :deep(.ant-select-dropdown) {
      background-color: #3a3a3a;
    }

    // 이미지 미리보기 컨테이너
    .image-preview-container {
      background-color: #3a3a3a;
      border: 1px solid #555;
      border-radius: 4px;
      padding: 8px;
    }
  }

  // 라이트 모드 스타일 (기본)
  &.light-mode {
    color: #333;
  }

  .button-list {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .button-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .image-preview {
    margin-top: 12px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    padding: 8px;

    img {
      width: 100%;
      max-height: 200px;
      object-fit: contain;
    }
  }

  .property-editor-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 24px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    &.dark {
      background: #3a3a3a;
      border-top: 1px solid #555;

      // 다크 모드 푸터 버튼 스타일
      :deep(.ant-btn-default) {
        background-color: #4a4a4a !important;
        color: #fff !important;
        border-color: #666 !important;

        &:hover {
          border-color: #40a9ff !important;
          color: #40a9ff !important;
        }
      }
    }

    &.light {
      background: #fff;
      border-top: 1px solid #f0f0f0;
    }
  }

  // Drawer body에 padding 추가
  :global(.ant-drawer-body) {
    padding-bottom: 80px !important;
  }

  // 노란색 버튼
  .yellow-button {
    background-color: #FFCC32;
    border-color: #FFCC32;

    &:hover {
      background-color: #FFB800;
      border-color: #FFB800;
    }
  }
}

// Popconfirm 스타일 오버라이드
:deep(.ant-popover-inner-content) {
  .ant-popconfirm-buttons {
    display: flex;
    flex-direction: row !important;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;

    .ant-btn {
      margin: 0 !important;
    }
  }
}
</style>